import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import ReactECharts from 'echarts-for-react';
import { FiHome, FiBarChart2, FiArrowRight, FiTrendingUp, FiMail, FiRadio, FiGlobe } from 'react-icons/fi';
import { IGlobalState } from '../../model/IGlobalState';
import { AnalyticsResult, HoldersChart } from '../../model/AnalyticsResult';
import api from '../../api/api';
import useCarousel from './useCarousel';
import Dropdown from './Dropdown';

// "2026-Q3" → "Jul–Sep 2026"
const quarterLabel = (q: string) => {
    const [y, n] = q.split('-Q').map(Number);
    const start = moment({ year: y, month: (n - 1) * 3, day: 1 });
    return `${start.format('MMM')}–${start.clone().add(2, 'months').format('MMM YYYY')}`;
};

const price = (v: number) => v.toFixed(v < 1 ? 3 : 2);

const HoldersPriceChart: React.FC<{ chart: HoldersChart }> = ({ chart }) => {
    const pts = chart.points;
    const holdersAt = (d: string) => pts.find(p => p.date === d)?.holders ?? null;
    const option = {
        animation: false,
        grid: { left: 44, right: 52, top: 16, bottom: 28 },
        tooltip: {
            trigger: 'axis',
            backgroundColor: '#0b1739', borderWidth: 0, padding: [8, 12],
            textStyle: { color: '#fff', fontSize: 12 },
            axisPointer: { type: 'line', lineStyle: { color: '#0f172a', type: 'dashed' } },
            formatter: (params: any[]) => {
                const p = pts[params[0].dataIndex];
                return `<strong>${moment(p.date).format('D MMM')}${p.close != null ? ` · ${price(p.close)}` : ''}</strong>`
                    + (p.holders != null ? `<br/><span style="color:#94a3b8;font-size:11px">${p.holders.toLocaleString()} shareholders</span>` : '');
            },
        },
        xAxis: {
            type: 'category', boundaryGap: false, data: pts.map(p => p.date),
            axisLine: { show: false }, axisTick: { show: false },
            axisLabel: { color: '#64748b', fontSize: 11, interval: Math.max(0, Math.ceil(pts.length / 6) - 1), formatter: (d: string) => moment(d).format('D MMM') },
        },
        yAxis: [
            { type: 'value', scale: true, splitLine: { lineStyle: { color: '#e5e9f0', type: 'dashed' } }, axisLabel: { color: '#64748b', fontSize: 11 } },
            { type: 'value', scale: true, splitLine: { show: false }, axisLabel: { color: '#0093ab', fontSize: 11, formatter: (v: number) => price(v) } },
        ],
        series: [
            {
                name: 'Share price', type: 'line', yAxisIndex: 1, showSymbol: false, data: pts.map(p => p.close),
                lineStyle: { color: '#00B5D1', width: 2 }, itemStyle: { color: '#00B5D1' },
                areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(0,181,209,0.3)' }, { offset: 1, color: 'rgba(0,181,209,0)' }] } },
            },
            {
                name: 'Shareholders', type: 'line', showSymbol: false, data: pts.map(p => p.holders),
                lineStyle: { color: '#0b1739', width: 2.5 }, itemStyle: { color: '#0b1739' },
            },
            {
                name: 'Announcement', type: 'scatter', symbolSize: 8, z: 5,
                itemStyle: { color: '#f59e0b', borderColor: '#fff', borderWidth: 2 },
                data: chart.markers.map(m => [m.date, holdersAt(m.date)]),
                label: {
                    show: true, position: 'top', distance: 10, align: 'left', offset: [-12, 0], // left-anchored so a marker near the start isn't clipped
                    formatter: (p: any) => { const m = chart.markers[p.dataIndex]; return `${moment(m.date).format('D MMM')} · ${m.label}`; },
                    backgroundColor: '#e6f8fb', color: '#0093ab', fontSize: 11, fontWeight: 600, padding: [4, 8], borderRadius: 6,
                },
                tooltip: { show: false },
            },
        ],
    };
    return <ReactECharts option={option} notMerge={true} style={{ height: 230 }} />;
};

const Analytics: React.FC = () => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const history = useHistory();
    const [quarter, setQuarter] = useState<string | null>(null); // null = current quarter to date
    const [data, setData] = useState<AnalyticsResult | null>(null);
    const hero = useCarousel<HTMLDivElement>([data]);
    const ticker = `${stock?.ticker}:${stock?.exchange}`;
    const open = (url: string | null) => { if (url) history.push(url); };
    // key result destinations, keyed on the link label; Website Hub links stay inert until that screen exists
    const linkUrl: Record<string, string> = {
        'See Registry': '/share-registry',
        'See Top Holders': '/share-registry',
        'See Campaigns': `/client-stocks/${ticker}/Email Marketing/Campaigns`,
        'See Announcements': '/communications',
    };

    useAsyncEffect(async (isMounted: () => boolean) => {
        if (!stock) return;
        const result = await api().getAnalytics(stock.id, quarter);
        if (isMounted()) setData(result);
    }, [stock?.id, quarter]);

    const maxSource = Math.max(1, ...(data?.sources.items ?? []).map(x => x.visitors));

    return <div className="v2-page dashboard-v2 analytics-v2">
        <div className="sub-tabs">
            <button type="button" onClick={() => history.push('/dashboard/overview')}><FiHome /> Overview</button>
            <button type="button" className="active"><FiBarChart2 /> Analytics</button>
        </div>

        <div className="page-header">
            <div>
                <h1>Analytics</h1>
                <p>How investors are engaging with {stock?.companyName} across the registry, announcements, email and the Website Hub</p>
            </div>
            {data && <div className="period">
                <Dropdown value={data.quarter} onChange={setQuarter}
                    options={data.quarters.map((q, i) => ({ value: q, label: quarterLabel(q) + (i === 0 ? ' (to date)' : '') }))} />
            </div>}
        </div>

        {data && <>
        <section className="glance">
            <img className="glance-bg" src="/images/login/brand-panel.jpg" alt="" />
            <h2>Key results · {data.period} compared with {data.previousPeriod}</h2>
            <div className="grid" ref={hero.ref} onScroll={hero.onScroll}>
                {data.keyResults.map(x => <div className="stat" key={x.label}>
                    <div className="value">{x.value} <span className="label">{x.label}</span></div>
                    <div className="sub">{x.detail}</div>
                    <button type="button" className="link" onClick={() => open(linkUrl[x.link] ?? null)}>{x.link} <FiArrowRight /></button>
                </div>)}
            </div>
            {hero.count > 1 && <div className="glance-dots">
                {Array.from({ length: hero.count }, (_, i) => <button key={i} type="button" aria-label={`Show results ${i + 1}`}
                    className={i === hero.index ? 'active' : ''} onClick={() => hero.goTo(i)} />)}
            </div>}
        </section>

        <div className="row row-2">
            <section className="card holders-chart">
                <header>
                    <span className="icon-tile"><FiTrendingUp /></span>
                    <h2>Shareholders vs Share Price</h2>
                    <div className="legend">
                        <span><i className="navy" /> Shareholders</span>
                        <span><i className="cyan" /> Share price</span>
                        <span><i className="amber" /> Announcement</span>
                    </div>
                </header>
                {data.chart.points.length > 1 ? <HoldersPriceChart chart={data.chart} /> : <p className="empty">No data for this quarter yet</p>}
                {data.chart.insight && <div className="insight"><span className="icon-tile"><FiTrendingUp /></span><p>{data.chart.insight}</p></div>}
            </section>

            <section className="card email">
                <header>
                    <span className="icon-tile"><FiMail /></span>
                    <div><h2>Email Engagement</h2><p className="sub">Opened and clicked, by campaign</p></div>
                </header>
                {data.email.map((x, i) => <div className="bar-row" key={i}>
                    <div className="line"><span className="name">{x.title}</span><span className="meta">{x.openedPct.toFixed(1)}% opened · {x.clickedPct.toFixed(1)}% clicked</span></div>
                    <div className="track"><span className="opened" style={{ width: `${x.openedPct}%` }} /><span className="clicked" style={{ width: `${x.clickedPct}%` }} /></div>
                </div>)}
                {data.email.length === 0 && <p className="empty">No campaigns sent this quarter</p>}
            </section>
        </div>

        <div className="row row-2">
            <section className="card top-announcements">
                <header>
                    <span className="icon-tile"><FiRadio /></span>
                    <h2>Which Announcements Got the Most Attention</h2>
                    <button type="button" className="link" onClick={() => open('/communications')}>All Announcements <FiArrowRight /></button>
                </header>
                <div className="table-wrap">
                    <table>
                        <thead><tr><th>Announcement</th><th>Date</th><th className="num">Views</th><th className="num">Email opens</th><th className="num">Price next day</th></tr></thead>
                        <tbody>
                            {data.announcements.map((x, i) => <tr key={i}>
                                <td className="headline">{x.headline}</td>
                                <td className="muted">{moment(x.date).format('D MMM')}</td>
                                <td className="num strong">{x.views.toLocaleString()}</td>
                                <td className="num muted">{x.emailOpens ?? '—'}</td>
                                <td className="num">{x.priceNextDayPct == null ? '—' : <span className={'pill ' + (x.priceNextDayPct > 0 ? 'ok' : x.priceNextDayPct < 0 ? 'bad' : '')}>
                                    {x.priceNextDayPct > 0 ? '+' : ''}{x.priceNextDayPct.toFixed(1)}%</span>}</td>
                            </tr>)}
                        </tbody>
                    </table>
                </div>
                {data.announcements.length === 0 && <p className="empty">No announcements this quarter</p>}
            </section>

            <section className="card sources">
                <header>
                    <span className="icon-tile"><FiGlobe /></span>
                    <div><h2>Visitors Sources</h2><p className="sub">{data.period}{data.sources.visitors != null && ` · ${data.sources.visitors.toLocaleString()} visitors`}</p></div>
                </header>
                {data.sources.items.map((x, i) => <div className="bar-row" key={x.name}>
                    <div className="line"><span className="name">{x.name}</span><span className="meta">{x.visitors.toLocaleString()} · {x.pct}%</span></div>
                    <div className="track"><span className={'fill tone-' + i} style={{ width: `${(x.visitors / maxSource) * 100}%` }} /></div>
                </div>)}
                {data.sources.items.length === 0 && <p className="empty">No visitor data yet</p>}
                <button type="button" className="link hub">Open Website Hub Analytics <FiArrowRight /></button>
            </section>
        </div>
        </>}
    </div>;
};

export default Analytics;
